'use client';

import SearchableCheckboxFilter from '../SearchableCheckboxFilter';
import CloseIcon from '@mui/icons-material/Close';
import style from './style.module.css';
import { handleQueryString } from '@/utils/queryParamsUtil';
import { useEffect, useState } from 'react';
import { FilterLoanListProps, Option } from './FilterLoanList.types';
import { LOAN_FILTERS } from './FilterLoanList.constants';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import MonetaryInput from '../MonetaryInput';
import {
  capitalizeFirstLetter,
  convertCurrencyToCents,
  convertToCurrencyValue,
} from '@/utils/format-currency';
import {
  getLoanFinancialInstitutions,
  getLoanType,
} from '@/services/loan/resources';
import { mapBanks, mapLoanTypes } from './FilterLoanList.utils';
import { useTranslations } from 'next-intl';

const UNIQUE_FILTERS = ['creditLimitFloor'];

let timeoutMonetaryValue;

const FilterLoanList = ({
  initialFiltersList = [],
  countryCode,
}: FilterLoanListProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const t = useTranslations('Index');

  const [loading, setLoading] = useState<boolean>(false);

  const [filters, setFilters] = useState<Option[]>(initialFiltersList);

  const [loanTypes, setLoanTypes] = useState<Option[]>([]);
  const [banks, setBanks] = useState<Option[]>([]);

  async function loadBanks() {
    const banksResponse = await getLoanFinancialInstitutions({
      country: countryCode,
    });
    const mappedBanks = mapBanks(banksResponse);
    setBanks(mappedBanks as Option[]);
  }

  async function loadLoanTypes() {
    const loanTypesResponse = await getLoanType({ country: countryCode });
    const mappedLoanTypes = mapLoanTypes(loanTypesResponse);
    setLoanTypes(mappedLoanTypes as Option[]);
  }

  const addActiveFilter = (option: Option) => {
    setLoading(true);
    setFilters((prevBenefits) => {
      const isUnique = UNIQUE_FILTERS.includes(option.category);
      const previous = prevBenefits || [];
      if (isUnique) {
        return [
          ...previous.filter(({ category }) => category !== option.category),
          option,
        ];
      }
      return [...previous, option];
    });
    const queryParams = handleQueryString(
      option.category === 'creditLimitFloor' ? 'set' : 'append',
      new URLSearchParams(searchParams),
      option.category,
      option.value
    );
    const newRoute = `${pathname}?${queryParams}`;
    router.push(newRoute);
  };

  const removeActiveFilter = (option: Option) => {
    setLoading(true);
    setFilters((prevBenefits) => {
      const isUnique = UNIQUE_FILTERS.includes(option.category);
      if (isUnique) {
        return prevBenefits.filter(
          ({ category }) => category !== option.category
        );
      }
      return prevBenefits.filter((filter) => filter.value !== option.value);
    });

    const queryParams = new URLSearchParams(searchParams);
    const filterValues = queryParams.getAll(option.category);
    const newFilterValues = filterValues.filter(
      (value) => value !== option.value
    );

    queryParams.delete(option.category);
    newFilterValues.forEach((value) => {
      queryParams.append(option.category, value);
    });

    const newRoute = `${pathname}?${queryParams}`;
    router.push(newRoute);
  };

  const clearAllFilters = () => {
    setLoading(true);
    setFilters([]);
    router.push(pathname);
  };

  const handleCreditLimitFloor = (value: string) => {
    if (timeoutMonetaryValue) window.clearTimeout(timeoutMonetaryValue);

    timeoutMonetaryValue = window.setTimeout(() => {
      const cleanValue = +value.replace(/\D/g, '');

      if (!cleanValue) {
        removeActiveFilter({
          value: convertCurrencyToCents(value),
          label: value,
          category: 'creditLimitFloor',
        });
        return;
      }
      addActiveFilter({
        value: convertCurrencyToCents(value),
        label: value,
        category: 'creditLimitFloor',
      });
    }, 1500);
  };

  const handleOptionLabel = (option: Option) => {
    const optionLabel = {
      bank: option.label,
      creditLimitFloor: convertToCurrencyValue(option.value),
      loanType: capitalizeFirstLetter(option.value),
    };
    return optionLabel[option.category];
  };

  const loanTypeFilters = () => {
    const mappedFilters = filters
      .filter(({ category }) => category === 'loanType')
      .map((f) => {
        return {
          ...f,
          label: capitalizeFirstLetter(f.value),
        };
      });
    return mappedFilters;
  };

  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const [isAnyFilterRendered, setIsAnyFilterRendered] = useState(false);

  useEffect(() => {
    const renderedFilters = filters.some((option) =>
      LOAN_FILTERS.includes(option.category)
    );
    setIsAnyFilterRendered(renderedFilters);
  }, [filters]);

  useEffect(() => {
    loadBanks();
    loadLoanTypes();
  }, []);

  // route change completed
  useEffect(() => {
    setLoading(false);
  }, [searchParams]);

  return (
    <div className={style.containerFilterLoan}>
      <div className={style.contentActiveFilterLoan}>
        <p className={style.titleFilterLoan}>{t('filter.loan.filterBy')}</p>
        <div className={style.contentActiveFilterTags}>
          {filters?.map((option) => {
            const shouldRender = LOAN_FILTERS.includes(option.category);
            if (!shouldRender) return;
            return (
              <div className={style.listItem} key={option.value}>
                {handleOptionLabel(option)}
                <span
                  role="button"
                  className={style.buttonRemove}
                  onClick={() => removeActiveFilter(option)}>
                  {mounted ? <CloseIcon /> : null}
                </span>
              </div>
            );
          })}
        </div>
        <div className={style.contentActiveFilterClear}>
          {isAnyFilterRendered && (
            <p className={style.clearAll} onClick={clearAllFilters}>
              {t('filter.loan.clearAll')}
            </p>
          )}
        </div>
      </div>
      {mounted ? (
        <div className={style.contentFilterLoan}>
          <SearchableCheckboxFilter
            selectedOptions={filters}
            onOptionDeselect={removeActiveFilter}
            onOptionSelect={addActiveFilter}
            placeholderSeach={t('filter.loan.select')}
            titleSearch={t('filter.loan.bank')}
            name="bank"
            allOptions={banks}
            disabled={!banks.length || loading}
          />
          <SearchableCheckboxFilter
            selectedOptions={loanTypeFilters()}
            onOptionDeselect={removeActiveFilter}
            onOptionSelect={addActiveFilter}
            allOptions={loanTypes}
            placeholderSeach={t('filter.loan.select')}
            titleSearch={t('filter.loan.typeOfLoan')}
            disabled={!loanTypes.length || loading}
            name="loanType"
          />

          <MonetaryInput
            titleInputSelect={t('filter.loan.creditLimit')}
            placeholder={t('filter.loan.value')}
            onChange={handleCreditLimitFloor}
            name="monetaryValue"
            value={convertToCurrencyValue(
              filters?.find(({ category }) => category === 'creditLimitFloor')
                ?.value || ''
            )}
            disabled={loading}
          />
        </div>
      ) : null}
    </div>
  );
};

export default FilterLoanList;
